import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useDailyTotals, useDailyItems, useDailyIrsaliyes, useMarkItemsManuallyReceived } from '@/hooks/use-eirsaliye';
import { useAuth } from '@/hooks/use-auth';
import { useProfitSettings } from '@/hooks/use-profit-settings';
import { useToast } from '@/hooks/use-toast';
import { eirsaliyeService } from '@/lib/eirsaliye-service';
import { generateDailyReport } from '@/lib/eirsaliye-daily-report';
import { FileDown, Calendar, Package, FileText, Hash, Loader2, CheckCircle2 } from 'lucide-react';

export function EirsaliyeDailyView() {
  const navigate = useNavigate();
  const { companyId } = useAuth();
  const { settings: profitSettings, loading: profitSettingsLoading } = useProfitSettings();
  const { toast } = useToast();

  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split('T')[0],
  );
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
  const [preparingImport, setPreparingImport] = useState(false);

  const { data: totals, isLoading: totalsLoading } = useDailyTotals(selectedDate);
  const { data: items, isLoading: itemsLoading } = useDailyItems(selectedDate);
  const { data: irsaliyes } = useDailyIrsaliyes(selectedDate);
  const markItemsManuallyReceivedMutation = useMarkItemsManuallyReceived();

  const handleDownloadReport = () => {
    if (!irsaliyes || !items) return;
    generateDailyReport(selectedDate, irsaliyes, items);
  };

  const importableItems = useMemo(
    () => (items || []).filter(item => !item.stock_imported && !!item.malzeme_no),
    [items],
  );
  const selectedSet = useMemo(() => new Set(selectedItemIds), [selectedItemIds]);
  const allImportableSelected = importableItems.length > 0 && importableItems.every(item => selectedSet.has(item.id));
  const closedCount = (items || []).filter(item => item.stock_imported).length;

  useEffect(() => {
    setSelectedItemIds([]);
  }, [selectedDate]);

  useEffect(() => {
    const validIds = new Set(importableItems.map(item => item.id));
    setSelectedItemIds(prev => prev.filter(id => validIds.has(id)));
  }, [importableItems]);

  const handleToggleItem = (itemId: string, checked: boolean) => {
    setSelectedItemIds(prev => {
      if (checked) return prev.includes(itemId) ? prev : [...prev, itemId];
      return prev.filter(id => id !== itemId);
    });
  };

  const handleToggleAll = (checked: boolean) => {
    if (checked) {
      setSelectedItemIds(importableItems.map(item => item.id));
      return;
    }
    setSelectedItemIds([]);
  };

  const handleImportToStock = async () => {
    if (!companyId) {
      toast({
        title: 'Sirket bilgisi bulunamadi',
        description: 'Lutfen tekrar giris yapin.',
        variant: 'destructive',
      });
      return;
    }

    if (!selectedItemIds.length) {
      toast({
        title: 'Kalem secilmedi',
        description: 'Stoga almak icin en az bir kalem secin.',
      });
      return;
    }

    if (profitSettingsLoading) {
      toast({
        title: 'Ayarlar yukleniyor',
        description: 'Mars fiyat tipi ayarlari yuklenirken lutfen tekrar deneyin.',
      });
      return;
    }

    setPreparingImport(true);
    try {
      const payload = await eirsaliyeService.prepareDailyStockImport(
        companyId,
        selectedDate,
        selectedItemIds,
        profitSettings.marsPriceTier,
      );
      if (!payload.length) {
        toast({
          title: 'Import edilecek kalem bulunamadi',
          description: 'Secilen kalemler daha once stoga alinmis olabilir.',
          variant: 'destructive',
        });
        return;
      }

      navigate('/stock-entry', {
        state: {
          fromEirsaliyeImport: true,
          eirsaliyeDate: selectedDate,
          eirsaliyeImportItems: payload,
        },
      });
    } catch (error) {
      toast({
        title: 'Hazirlama hatasi',
        description: error instanceof Error ? error.message : 'Stok import hazirlanamadi.',
        variant: 'destructive',
      });
    } finally {
      setPreparingImport(false);
    }
  };

  const handleMarkManuallyReceived = async () => {
    if (!selectedItemIds.length) {
      toast({
        title: 'Kalem secilmedi',
        description: 'Manuel kapatmak icin en az bir kalem secin.',
      });
      return;
    }

    try {
      await markItemsManuallyReceivedMutation.mutateAsync(selectedItemIds);
      setSelectedItemIds([]);
    } catch {
      // Error handling is managed in the mutation hook.
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calendar className="w-5 h-5" />
            Gunluk Ozet
          </CardTitle>
          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-40 h-9"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadReport}
              disabled={!irsaliyes?.length}
            >
              <FileDown className="w-4 h-4 mr-1" />
              PDF Indir
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Daily summary badges */}
        <div className="flex flex-wrap gap-3">
          <Badge variant="secondary" className="text-sm px-3 py-1">
            <FileText className="w-3.5 h-3.5 mr-1.5" />
            {totalsLoading ? '...' : totals?.irsaliye_count ?? 0} Irsaliye
          </Badge>
          <Badge variant="secondary" className="text-sm px-3 py-1">
            <Package className="w-3.5 h-3.5 mr-1.5" />
            {totalsLoading ? '...' : totals?.total_adet ?? 0} Adet
          </Badge>
          <Badge variant="secondary" className="text-sm px-3 py-1">
            <Hash className="w-3.5 h-3.5 mr-1.5" />
            {totalsLoading ? '...' : totals?.unique_products ?? 0} Urun
          </Badge>
        </div>

        {/* Daily items table */}
        {itemsLoading ? (
          <div className="text-center py-6 text-muted-foreground text-sm">Yukleniyor...</div>
        ) : !items?.length ? (
          <div className="text-center py-6 text-muted-foreground text-sm">
            <Package className="w-8 h-8 mx-auto mb-2 opacity-30" />
            Bu tarihte irsaliye bulunamadi.
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3 p-3 rounded-lg border bg-muted/30">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={allImportableSelected}
                  onCheckedChange={(checked) => handleToggleAll(checked === true)}
                  disabled={!importableItems.length}
                />
                <span className="text-sm font-medium">Tum uygun kalemleri sec</span>
              </div>

              <Button
                size="sm"
                onClick={handleImportToStock}
                disabled={
                  preparingImport ||
                  markItemsManuallyReceivedMutation.isPending ||
                  selectedItemIds.length === 0 ||
                  profitSettingsLoading
                }
              >
                {preparingImport ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    Hazirlaniyor...
                  </>
                ) : (
                  <>Urunleri Stoga Al ({selectedItemIds.length})</>
                )}
              </Button>

              <Button
                size="sm"
                variant="secondary"
                onClick={handleMarkManuallyReceived}
                disabled={
                  preparingImport ||
                  markItemsManuallyReceivedMutation.isPending ||
                  selectedItemIds.length === 0
                }
              >
                {markItemsManuallyReceivedMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    Isleniyor...
                  </>
                ) : (
                  <>Manuel Alindi ({selectedItemIds.length})</>
                )}
              </Button>

              {closedCount > 0 && (
                <Badge variant="secondary" className="text-xs">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  {closedCount} kalem kapatildi
                </Badge>
              )}
            </div>

            <ScrollArea className="h-72">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-muted-foreground text-xs">
                    <th className="text-left pb-2 pr-2 w-8">Sec</th>
                    <th className="text-left pb-2 pr-3">Irsaliye No</th>
                    <th className="text-left pb-2 pr-3">Malzeme No</th>
                    <th className="text-left pb-2 pr-3">Malzeme Tanimi</th>
                    <th className="text-right pb-2 pr-3">Miktar</th>
                    <th className="text-left pb-2 pr-3">Birim</th>
                    <th className="text-left pb-2">Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, i) => {
                    const selectable = !item.stock_imported && !!item.malzeme_no;
                    return (
                      <tr key={item.id || i} className="border-b last:border-0 hover:bg-muted/40 transition-colors">
                        <td className="py-1.5 pr-2">
                          <Checkbox
                            checked={selectedSet.has(item.id)}
                            disabled={!selectable}
                            onCheckedChange={(checked) => handleToggleItem(item.id, checked === true)}
                          />
                        </td>
                        <td className="py-1.5 pr-3 font-mono text-xs">{item.irsaliye_no}</td>
                        <td className="py-1.5 pr-3 font-mono text-xs">{item.malzeme_no || '-'}</td>
                        <td className="py-1.5 pr-3">{item.malzeme_tanimi}</td>
                        <td className="py-1.5 pr-3 text-right font-medium">{item.miktar}</td>
                        <td className="py-1.5 pr-3">{item.olcu_birimi}</td>
                        <td className="py-1.5">
                          {item.stock_imported ? (
                            item.received_mode === 'manual_received' ? (
                              <Badge variant="secondary" className="text-[10px]">Manuel Alindi</Badge>
                            ) : (
                              <Badge variant="default" className="text-[10px]">Stoga Alindi</Badge>
                            )
                          ) : !item.malzeme_no ? (
                            <Badge variant="destructive" className="text-[10px]">Malzeme No Eksik</Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px]">Bekliyor</Badge>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </ScrollArea>
          </>
        )}
      </CardContent>
    </Card>
  );
}
